import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Tooltip,
  TablePagination,
  Collapse,
} from "@mui/material";

import {
  MdFilterList,
  MdRefresh,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
} from "react-icons/md";

import { GiSpades } from "react-icons/gi";

import {
  FaChrome,
  FaFirefoxBrowser,
  FaSafari,
  FaEdge,
  FaWindows,
  FaApple,
  FaAndroid,
  FaLinux,
  FaDesktop,
  FaMobileAlt,
  FaTabletAlt,
} from "react-icons/fa";

import { IoGlobeOutline } from "react-icons/io5";

import axios from "axios";
import config from "../config";
import moment from "moment";
import UAParser from "ua-parser-js";

const baseUrl = config.baseURL;

// --- Enterprise Style Constants ---
const EnterpriseColors = {
  Primary: "#2E4053",
  Secondary: "#9CA3AF",
  Background: "#F9FAFB",
  CardBg: "#FFFFFF",
  TextDark: "#1F2937",
  Border: "#D1D5DB",
  Success: "#10B981",
  Info: "#3B82F6",
  Danger: "#EF4444",
  Warning: "#FBBF24",
  LightBg: "#F3F4F6",
};

// --- Helper Functions ---
const getDeviceIcon = (info) => {
  const iconProps = { size: 16, style: { marginRight: "5px" } };
  if (!info) return <FaDesktop {...iconProps} />;

  if (info.device?.type) {
    const type = info.device.type.toLowerCase();
    if (type === "mobile") return <FaMobileAlt {...iconProps} />;
    if (type === "tablet") return <FaTabletAlt {...iconProps} />;
  }

  if (info.os?.name) {
    const osName = info.os.name.toLowerCase();
    if (osName.includes("windows"))
      return (
        <FaWindows
          {...iconProps}
          style={{ color: "#00A4EF", marginRight: "5px" }}
        />
      );
    if (osName.includes("mac") || osName.includes("ios"))
      return <FaApple {...iconProps} />;
    if (osName.includes("android"))
      return (
        <FaAndroid
          {...iconProps}
          style={{ color: "#3DDC84", marginRight: "5px" }}
        />
      );
    if (osName.includes("linux")) return <FaLinux {...iconProps} />;
  }

  if (info.browser?.name) {
    const browserName = info.browser.name.toLowerCase();
    if (browserName.includes("chrome"))
      return (
        <FaChrome
          {...iconProps}
          style={{ color: "#4285F4", marginRight: "5px" }}
        />
      );
    if (browserName.includes("firefox"))
      return (
        <FaFirefoxBrowser
          {...iconProps}
          style={{ color: "#FF7139", marginRight: "5px" }}
        />
      );
    if (browserName.includes("safari"))
      return (
        <FaSafari
          {...iconProps}
          style={{ color: "#1B76BB", marginRight: "5px" }}
        />
      );
    if (browserName.includes("edge"))
      return (
        <FaEdge
          {...iconProps}
          style={{ color: "#0078D7", marginRight: "5px" }}
        />
      );
  }

  return <FaDesktop {...iconProps} />;
};

const getDeviceDisplayName = (info) => {
  if (!info) return "N/A";
  if (info.device?.model) return info.device.model;
  if (info.os?.name) return `${info.os.name} ${info.os.version || ""}`;
  if (info.browser?.name) return info.browser.name;
  return "Unknown Device";
};

const getLocationDisplay = (geo) => {
  if (!geo || !geo.country) {
    return { flag: <IoGlobeOutline size={16} />, name: "N/A", tooltip: "" };
  }

  const countryCode = geo.country.toLowerCase();
  const city = geo.city || geo.region;
  const flagUrl = `https://flagcdn.com/16x12/${countryCode}.png`;

  return {
    flag: (
      <img
        src={flagUrl}
        alt={countryCode}
        style={{
          width: 16,
          height: 12,
          marginRight: 5,
          border: "1px solid #ccc",
        }}
      />
    ),
    name: city || countryCode.toUpperCase(),
    tooltip: `${geo.city || ""}, ${
      geo.region || ""
    }, ${countryCode.toUpperCase()}`,
  };
};

// --- Row Details Component ---
const RowDetails = ({ details }) => {
  const [open, setOpen] = useState(false);
  let parsedDetails = {};
  try {
    parsedDetails = typeof details === "string" ? JSON.parse(details) : details;
  } catch {
    parsedDetails = details || {};
  }

  return (
    <Box sx={{ margin: 1 }}>
      <Typography variant="body2">
        <IconButton
          size="small"
          onClick={() => setOpen(!open)}
          sx={{ padding: 0, marginRight: 1 }}
        >
          {open ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
        </IconButton>
        <span style={{ fontWeight: 600 }}>Action Details</span>
      </Typography>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box
          sx={{
            padding: 2,
            marginTop: 1,
            backgroundColor: EnterpriseColors.LightBg,
            border: `1px solid ${EnterpriseColors.Border}`,
            borderRadius: 1,
            maxHeight: 200,
            overflowY: "auto",
          }}
        >
          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
              fontSize: "12px",
              color: EnterpriseColors.TextDark,
              margin: 0,
            }}
          >
            {JSON.stringify(parsedDetails, null, 2)}
          </pre>
        </Box>
      </Collapse>
    </Box>
  );
};

// --- Single Audit Log Row ---
const AuditLogRow = ({ log }) => {
  const [showDetails, setShowDetails] = useState(false);
  const uaInfo = log.user_agent
    ? new UAParser(log.user_agent).getResult()
    : null;
  const deviceName = getDeviceDisplayName(uaInfo);
  const deviceIcon = getDeviceIcon(uaInfo);

  const geoInfo = log.geo_ip || {}; // backend should send geo_ip
  const {
    flag,
    name: locationName,
    tooltip: locationTooltip,
  } = getLocationDisplay(geoInfo);

  const actionText = log.action ? log.action.toUpperCase() : "UNKNOWN";

  const getActionColor = (action) => {
    switch (action) {
      case "CREATE":
        return EnterpriseColors.Success;
      case "UPDATE":
        return EnterpriseColors.Info;
      case "DELETE":
        return EnterpriseColors.Danger;
      case "LOGIN":
        return EnterpriseColors.Warning;
      case "LOGOUT":
        return EnterpriseColors.Secondary;
      default:
        return EnterpriseColors.TextDark;
    }
  };

  return (
    <>
      <TableRow
        hover
        onClick={() => setShowDetails(!showDetails)}
        sx={{
          "& > *": { borderBottom: "unset" },
          cursor: "pointer",
          backgroundColor: showDetails
            ? EnterpriseColors.LightBg
            : EnterpriseColors.CardBg,
        }}
      >
        <TableCell>
          <IconButton size="small">
            {showDetails ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 500, color: EnterpriseColors.TextDark }}>
          {moment(log.created_at).format("YYYY-MM-DD HH:mm:ss")}
        </TableCell>
        <TableCell sx={{ fontWeight: 500, color: EnterpriseColors.TextDark }}>
          {log.user_name || "System"}
        </TableCell>
        <TableCell>
          <span style={{ color: getActionColor(log.action), fontWeight: 600 }}>
            {actionText}
          </span>
        </TableCell>
        <TableCell>
          <Tooltip title={`OS/Browser: ${deviceName}`}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {deviceIcon}
              {deviceName.substring(0, 20)}
            </Box>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Tooltip title={locationTooltip || "No GeoIP data available"}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {flag}
                {locationName}
              </Box>
            </Box>
          </Tooltip>
        </TableCell>
        <TableCell>
          {log.module_name === null
            ? "System"
            : log.module_name || log.module_name}
        </TableCell>
        <TableCell>{log.record_id || "N/A"}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={8} sx={{ paddingBottom: 0, paddingTop: 0 }}>
          <Collapse in={showDetails} timeout="auto" unmountOnExit>
            <RowDetails details={log.details} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// --- Main AuditLogs Component ---
const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(false);

  // Filters mapped to backend
  const [userNameFilter, setUserNameFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [moduleIdFilter, setModuleIdFilter] = useState("");
  const [recordIdFilter, setRecordIdFilter] = useState("");
  const [ipAddressFilter, setIpAddressFilter] = useState("");
  const [userAgentFilter, setUserAgentFilter] = useState("");

  const actionOptions = [
    "create",
    "update",
    "delete",
    "login",
    "logout",
    "permission_change",
  ];

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setLogs([]);
        setTotalCount(0);
        return;
      }

      const params = {
        page: page + 1,
        limit,
        user_name: userNameFilter || undefined,
        action: actionFilter ? actionFilter.toUpperCase() : undefined, // <-- convert to uppercase
        module_name: moduleIdFilter || undefined,
        record_id: recordIdFilter || undefined,
        ip_address: ipAddressFilter || undefined,
        user_agent: userAgentFilter || undefined,
      };
      console.log("params", params);
      Object.keys(params).forEach(
        (key) => params[key] === undefined && delete params[key]
      );

      const res = await axios.get(`${baseUrl}/api/auditlogs`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("res", res);
      setLogs(res.data.data || []);
      setTotalCount(res.data.totalCount || res.data.count || 0);
    } catch (err) {
      console.error("Failed to fetch audit logs:", err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [
    page,
    limit,
    userNameFilter,
    actionFilter,
    moduleIdFilter,
    recordIdFilter,
    ipAddressFilter,
    userAgentFilter,
  ]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    switch (name) {
      case "user_name":
        setUserNameFilter(value);
        break;
      case "action":
        setActionFilter(value);
        break;
      case "module_name":
        setModuleIdFilter(value);
        break;
      case "record_id":
        setRecordIdFilter(value);
        break;
      case "ip_address":
        setIpAddressFilter(value);
        break;
      case "user_agent":
        setUserAgentFilter(value);
        break;
      default:
        break;
    }
    setPage(0);
  };

  const handleReload = () => {
    setUserNameFilter("");
    setActionFilter("");
    setModuleIdFilter("");
    setRecordIdFilter("");
    setIpAddressFilter("");
    setUserAgentFilter("");
    setPage(0);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box
      sx={{
        padding: 4,
        backgroundColor: EnterpriseColors.Background,
        minHeight: "100vh",
        width: "100%",
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 600,
          color: EnterpriseColors.TextDark,
          display: "flex",
          alignItems: "center",
          marginBottom: 3,
        }}
      >
        <GiSpades
          size={40}
          style={{ marginRight: 10, color: EnterpriseColors.Primary }}
        />
        Audit Logs
      </Typography>

      {/* Filter Bar */}
      <Paper
        sx={{
          display: "flex",
          gap: 2,
          padding: 2,
          marginBottom: 3,
          alignItems: "center",
          flexWrap: "wrap",
          backgroundColor: EnterpriseColors.CardBg,
          boxShadow: 2,
        }}
      >
        <MdFilterList size={24} color={EnterpriseColors.Secondary} />

        <TextField
          label="Filter by User Name"
          name="user_name"
          variant="outlined"
          size="small"
          value={userNameFilter}
          onChange={handleFilterChange}
          sx={{ minWidth: 180 }}
        />
        <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Action Type</InputLabel>
          <Select
            label="Action Type"
            name="action"
            value={actionFilter}
            onChange={handleFilterChange}
          >
            <MenuItem value="">
              <em>All Actions</em>
            </MenuItem>
            {actionOptions.map((action) => (
              <MenuItem key={action} value={action}>
                {action.toUpperCase()}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Filter by Module "
          name="module_name"
          variant="outlined"
          size="small"
          value={moduleIdFilter}
          onChange={handleFilterChange}
          sx={{ minWidth: 150 }}
        />
        <TextField
          label="Filter by Record ID"
          name="record_id"
          variant="outlined"
          size="small"
          value={recordIdFilter}
          onChange={handleFilterChange}
          sx={{ minWidth: 150 }}
        />
        <TextField
          label="Filter by IP Address"
          name="ip_address"
          variant="outlined"
          size="small"
          value={ipAddressFilter}
          onChange={handleFilterChange}
          sx={{ minWidth: 150 }}
        />
        <TextField
          label="Filter by Device"
          name="user_agent"
          variant="outlined"
          size="small"
          value={userAgentFilter}
          onChange={handleFilterChange}
          sx={{ minWidth: 150, flexGrow: 1 }}
        />

        <Tooltip title="Clear Filters and Reload Logs">
          <IconButton onClick={handleReload} disabled={loading}>
            <MdRefresh
              size={24}
              style={{
                color: loading
                  ? EnterpriseColors.Secondary
                  : EnterpriseColors.Primary,
              }}
            />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* Table */}
      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 3 }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader aria-label="audit logs table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell
                  style={{ fontWeight: 600, color: EnterpriseColors.TextDark }}
                >
                  Timestamp
                </TableCell>
                <TableCell
                  style={{ fontWeight: 600, color: EnterpriseColors.TextDark }}
                >
                  User Name
                </TableCell>
                <TableCell
                  style={{ fontWeight: 600, color: EnterpriseColors.TextDark }}
                >
                  Action
                </TableCell>
                <TableCell
                  style={{ fontWeight: 600, color: EnterpriseColors.TextDark }}
                >
                  Device / OS
                </TableCell>
                <TableCell
                  style={{ fontWeight: 600, color: EnterpriseColors.TextDark }}
                >
                  Location / IP
                </TableCell>
                <TableCell
                  style={{ fontWeight: 600, color: EnterpriseColors.TextDark }}
                >
                  Module
                </TableCell>
                <TableCell
                  style={{ fontWeight: 600, color: EnterpriseColors.TextDark }}
                >
                  Record ID
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography
                      style={{ padding: 2, color: EnterpriseColors.Secondary }}
                    >
                      Loading...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography
                      style={{ padding: 2, color: EnterpriseColors.Secondary }}
                    >
                      No audit logs found matching the filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log, index) => (
                  <AuditLogRow key={log.id || index} log={log} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={limit}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            color: EnterpriseColors.TextDark,
            borderTop: `1px solid ${EnterpriseColors.Border}`,
          }}
        />
      </Paper>
    </Box>
  );
};

export default AuditLogs;
